import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { FormTemplateModal } from "@components/modals/FormTemplateModal";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState } from "react";
import { formTemplatesApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { defaultFormTemplates } from "./defaultFormTemplates";

export const FormTemplates = () => {
  const formTemplates = useProjectStore((state) => state.formTemplates);
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);

  const handleCreateDefaults = async () => {
    if (!userData?.org_id) return;

    setIsCreatingDefaults(true);
    try {
      let created = 0;
      for (const template of defaultFormTemplates) {
        const result = await formTemplatesApi.create({
          name: template.name,
          org_id: userData.org_id,
          categories: template.categories,
        });
        useProjectStore.getState().setFormTemplates([
          ...useProjectStore.getState().formTemplates,
          result,
        ]);
        created++;
      }
      showNotification({
        type: "success",
        message: `${created} modèle(s) par défaut ajouté(s)`,
      });
    } catch (error) {
      console.error("Error creating default form templates:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création des modèles",
      });
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  const getTemplateCounts = (template: any) => {
    const categories = template.categories?.length || 0;
    let fields = 0;
    (template.categories || []).forEach((cat: any) => {
      fields += cat.fields?.length || 0;
    });
    return { categories, fields };
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 ml-10">
      {(selectedTemplate || newTemplateOpen) && (
        <FormTemplateModal
          templateId={selectedTemplate || ""}
          onClose={() => {
            setSelectedTemplate(null);
            setNewTemplateOpen(false);
          }}
        />
      )}
      <div className="flex flex-col w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Modèles de formulaires</Text1>
          <Text2>Configurez vos modèles de formulaires ici.</Text2>
          <Text3>
            Les modèles de formulaires définissent les catégories et champs
            utilisés lors de la génération de comptes-rendus.
          </Text3>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-400">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="compte-rendu, réception..."
            />
          </div>
          {formTemplates.length === 0 && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={handleCreateDefaults}
              disabled={isCreatingDefaults}
            >
              {isCreatingDefaults
                ? "Création en cours..."
                : "Ajouter les modèles par défaut"}
            </SecondaryButton>
          )}
        </div>

        <div className="flex flex-col divide-y mt-4">
          {formTemplates
            .filter((template) =>
              template.name.toLowerCase().includes(value.toLowerCase()),
            )
            .map((template, index) => {
              const counts = getTemplateCounts(template);
              return (
                <div
                  key={index}
                  className="flex flex-row rounded-xl items-center px-4 py-2"
                >
                  <div className="flex flex-col w-250">
                    <div className="text-h2 font-medium">{template.name}</div>
                  </div>
                  <div className="text-h3 text-neutral-400 mr-auto flex flex-row gap-3">
                    <span>{counts.categories} catégorie(s)</span>
                    <span>{counts.fields} champ(s)</span>
                  </div>
                  <SecondaryButton
                    label="Modifier"
                    variant="small"
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                </div>
              );
            })}
        </div>

        <Button
          className="mt-4"
          label="Nouveau modèle de formulaire"
          onClick={() => setNewTemplateOpen(true)}
        />
      </div>
    </div>
  );
};
